import { useEffect, useMemo, useState } from 'react'
import {
  MRT_PaginationState,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_ColumnFiltersState,
} from 'mantine-react-table'
import { UnitListItem } from '@/shared/api/topli/TopliApi'
import { useUnitsByPage } from '@/entities/Unit'
import { Badge, ActionIcon as MantineActionButton, Text } from '@mantine/core'
import { Checkbox } from '@/shared/ui/Checkbox/Checkbox'
import { Camera, CameraOff } from 'lucide-react'
import {
  getCurrentTranslation,
  displayPriceInLocalFormat,
  findLabelByValue,
  getUnitPriceData,
} from '@/shared/lib/utils'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { useDebounce } from '@/shared/lib/useDebounce'
import { TruncatedTextCell } from '@/shared/ui/TruncatedTextCellProps'
import { useTableFilters } from '@/shared/hooks/useTableFilters'
import { digitsOnlyFilterProps } from '@/shared/lib/tableFilterUtils'

export const useUnitsTable = (
  onOpenPhotos: (row: UnitListItem) => void,
  onOpenFullDescription: (row: UnitListItem) => void,
) => {
  const { t } = useTranslation(['unit', 'common'])
  const { language } = useLanguageStore()
  const { conditionOptions, accommodationTypeOptions } = useTranslatedEnums()
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [isShowLocalPrices, setIsShowLocalPrices] = useState(false)
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { getTextFilter } = useTableFilters(columnFilters)

  const debouncedSearchById = useDebounce(getTextFilter('id') || '', 500)
  const debouncedSearch = useDebounce(search, 500)

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const {
    data: units,
    isLoading: isLoadingUnits,
    isError: isErrorUnits,
    isFetching: isFetchingUnits,
  } = useUnitsByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    debouncedSearch,
    language,
    debouncedSearchById,
  )

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  const fetchedUnits = units?.data ?? []
  const totalRowCount = units?.total ?? 0

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<UnitListItem>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        filterVariant: 'text',
        ...digitsOnlyFilterProps(),
        size: 90,
      },
      {
        accessorKey: 'name',
        enableColumnFilter: false,
        header: t('unit:name'),
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {getCurrentTranslation(row.original, language, 'name')}
          </div>
        ),
      },
      {
        accessorKey: 'photos',
        enableColumnFilter: false,
        header: t('unit:photos'),
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.photos ? (
            <MantineActionButton variant='light' onClick={() => onOpenPhotos(row.original)}>
              <Camera />
            </MantineActionButton>
          ) : (
            <CameraOff color='#c5cad0' />
          ),
      },
      {
        accessorKey: 'type',
        enableColumnFilter: false,
        header: t('common:library_items.accommodation_type.sing'),
        Cell: ({ row }) => {
          return row.original.type ? (
            <Badge size='sm'>{findLabelByValue(accommodationTypeOptions, row.original.type)}</Badge>
          ) : (
            '–'
          )
        },
      },
      {
        accessorKey: 'prices',
        enableColumnFilter: false,
        header: t('unit:price'),
        size: 280,
        Cell: ({ row }) => {
          const { price, currentCurrency } = getUnitPriceData(
            row.original.prices,
            isShowLocalPrices,
          )
          return (
            <>
              {price && (
                <div className='text-black text-left font-medium'>
                  {displayPriceInLocalFormat(price, currentCurrency, 100)}
                </div>
              )}
            </>
          )
        },
      },
      {
        accessorKey: 'fee',
        enableColumnFilter: false,

        size: 280,
        header: `${t('unit:commission_for_international_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.fee != null ? row.original.fee : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'fee_local',
        enableColumnFilter: false,
        size: 280,
        header: `${t('unit:commission_for_local_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.fee != null ? row.original.fee_local : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'agent_fee',
        enableColumnFilter: false,
        size: 280,
        header: `${t('unit:agent_commission_for_international_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.agent_fee != null ? row.original.agent_fee : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'agent_fee_local',
        enableColumnFilter: false,
        size: 280,
        header: `${t('unit:agent_commission_for_local_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.agent_fee_local != null ? row.original.agent_fee_local : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'bathrooms',
        enableColumnFilter: false,
        header: t('unit:bathrooms'),
      },
      {
        accessorKey: 'bedrooms',
        enableColumnFilter: false,
        header: t('unit:bedrooms'),
      },
      {
        accessorKey: 'level',
        enableColumnFilter: false,
        header: t('unit:level'),
      },
      {
        accessorKey: 'size',
        enableColumnFilter: false,
        header: t('unit:gross_size'),
      },
      {
        accessorKey: 'net_size',
        enableColumnFilter: false,
        header: t('unit:net_size'),
      },
      {
        accessorKey: 'lotSize',
        enableColumnFilter: false,
        header: t('unit:lot_size'),
      },
      {
        accessorKey: 'condition',
        enableColumnFilter: false,
        header: t('common:library_items.condition.sing'),
        Cell: ({ row }) => {
          return row.original.condition ? (
            <Badge size='sm'>{findLabelByValue(conditionOptions, row.original.condition)}</Badge>
          ) : (
            '–'
          )
        },
      },
      {
        accessorKey: 'building_id',
        enableColumnFilter: false,
        header: t('unit:building'),
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {getCurrentTranslation(row.original.building, language, 'name')}
          </div>
        ),
      },
      {
        accessorKey: 'amenities',
        enableColumnFilter: false,
        header: t('unit:amenities'),
        size: 300,
        Cell: ({ row }) => (
          <div className='flex flex-row flex-wrap gap-1 w-full'>
            {row.original.amenities.length > 0
              ? row.original.amenities.map(amenity => (
                  <Badge key={amenity.id} size='sm' color='gold'>
                    {getCurrentTranslation(amenity, language, 'name')}
                  </Badge>
                ))
              : '–'}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        enableColumnFilter: false,
        header: t('unit:description'),
        size: 380,
        Cell: ({ row }) => {
          const currentDescription = getCurrentTranslation(row.original, language, 'description')
          return (
            <TruncatedTextCell
              text={currentDescription}
              onShowMore={() => onOpenFullDescription(row.original)}
            />
          )
        },
      },
      {
        accessorKey: 'activity',
        enableColumnFilter: false,
        header: t('unit:activity'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.activity} readOnly />,
      },
      {
        accessorKey: 'default_collection',
        enableColumnFilter: false,
        header: t('unit:default_collection'),
        Cell: ({ row }) => <Checkbox checked={!!row.original?.default_collection} readOnly />,
      },
      {
        accessorKey: 'hide_price',
        enableColumnFilter: false,
        header: t('unit:hide_price'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.hide_price} readOnly />,
      },
      {
        accessorKey: 'ai_translations',
        enableColumnFilter: false,
        header: t('unit:machine_translation'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.has_ai_translation} readOnly />,
      },
      {
        accessorKey: 'checked_manual',
        enableColumnFilter: false,
        header: t('unit:checked_manually'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.checked_manual} readOnly />,
      },
      {
        accessorKey: 'pdf',
        enableColumnFilter: false,
        header: 'PDF',
        enableSorting: false,
        Cell: ({ row }) => {
          const currentId = row.original.id
          return (
            <a
              href={`${
                import.meta.env.VITE_WEB_APP_URL
              }announcements/apartments/${currentId}?download_modal`}
              className='text-sm font-medium text-blue hover:underline underline-offset-4'
              target='_blank'
            >
              pdf
            </a>
          )
        },
      },
    ],
    [isShowLocalPrices, onOpenPhotos, language],
  )
  return {
    pagination,
    fetchedUnits,
    totalRowCount,
    columns,
    isErrorUnits,
    isFetchingUnits,
    isLoadingUnits,
    sorting,
    isShowLocalPrices,
    setPagination,
    setSorting,
    setIsShowLocalPrices,
    handleSearchChange,
    columnFilters,
    setColumnFilters,
  }
}
